// AppHeader — global site header rendered above every route by AppShell.
//
// A dark translucent header with the original display wordmark and cyan edge.
//
// Layout:
//   - Left: "LiveVane" wordmark → Link to '/'.
//   - Center (>=640px): primary nav. Streams is public; Flows and
//     Compositions need a session; Admin needs an admin role (matches the
//     gate in `routes/admin/route.tsx`). Account lives in the user menu,
//     and server tools live under Super admin.
//   - Right: UserMenu when authenticated, else a single "Sign in" link.
//
// Responsive:
//   - On viewports <640px the horizontal nav is hidden and the same links
//     are rendered inside the UserMenu via `mobileSlot`. Unauthenticated
//     visitors on mobile see the brand + Sign in.
//
// Accessibility:
//   - Each nav link is a real <Link>; the active one carries aria-current.
//   - Header role defaults to `banner` via the native <header> element.
import type { ReactElement } from 'react'
import { Link } from '@tanstack/react-router'

import { Button } from '@/components/ui/button'
import { useAuthUser, useIsAuthenticated } from '@/stores/auth-store'
import { cn } from '@/lib/utils'

import { UserMenu } from './user-menu'

type NavTarget = '/streams' | '/flows' | '/admin'

function HeaderNavLink({
  to,
  children,
}: {
  to: NavTarget
  children: React.ReactNode
}): ReactElement {
  return (
    <Link
      to={to}
      activeProps={{
        className: 'text-foreground border-cyber-cyan',
        'aria-current': 'page',
      }}
      inactiveProps={{ className: 'text-foreground/60 border-transparent' }}
      className={cn(
        'inline-flex h-14 items-center border-b-2 px-3 text-sm font-medium transition-colors duration-150 hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
      )}
    >
      {children}
    </Link>
  )
}

/**
 * Mobile-only version of the nav link, rendered inside the UserMenu
 * popover via the menu's `mobileSlot`. Uses `role="menuitem"` so the
 * parent menu's delegated-close picks it up.
 */
function MobileNavLink({
  to,
  children,
}: {
  to: NavTarget
  children: React.ReactNode
}): ReactElement {
  return (
    <Link
      to={to}
      role="menuitem"
      activeProps={{
        className: 'text-cyber-cyan',
        'aria-current': 'page',
      }}
      className={cn(
        'block rounded-lg px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/60',
        'focus-visible:outline-none focus-visible:bg-muted/60',
      )}
    >
      {children}
    </Link>
  )
}

export function AppHeader(): ReactElement {
  const isAuthenticated = useIsAuthenticated()
  const user = useAuthUser()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  const mobileNav = (
    <div className="sm:hidden">
      <MobileNavLink to="/streams">Streams</MobileNavLink>
      {isAuthenticated ? <MobileNavLink to="/flows">Flows</MobileNavLink> : null}
      {isAdmin ? <MobileNavLink to="/admin">Admin</MobileNavLink> : null}
    </div>
  )

  return (
    <header
      data-testid="app-header"
      className="studio-header sticky top-0 z-30 w-full border-b border-cyber-cyan/20"
    >
      <div className="mx-auto flex h-14 w-full max-w-[1536px] items-center justify-between gap-3 px-4 sm:px-6">
        <Link
          to="/"
          aria-label="LiveVane home"
          className={cn(
            'studio-wordmark shrink-0 rounded-sm text-cyber-cyan',
            'transition-colors hover:text-cyber-cyan/80',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          )}
          data-testid="app-header-brand"
        >
          LiveVane
        </Link>

        <nav
          aria-label="Primary"
          className="hidden flex-1 items-center justify-center sm:flex"
        >
          <HeaderNavLink to="/streams">Streams</HeaderNavLink>
          {isAuthenticated ? <HeaderNavLink to="/flows">Flows</HeaderNavLink> : null}
          {isAdmin ? <HeaderNavLink to="/admin">Admin</HeaderNavLink> : null}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          {isAuthenticated ? (
            <UserMenu mobileSlot={mobileNav} />
          ) : (
            <Button
              asChild
              variant="ghost"
              size="sm"
              data-testid="app-header-signin"
            >
              <Link to="/login">Sign in</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  )
}

export default AppHeader
